/**
 * Lista de períodos da aba Ocupação (mês, quinzena ou semana).
 *
 * "Agora e próximos": quanto da capacidade de cada período a fila de hoje já
 * toma, com a faixa de erro e UM ponto de cor (pelo pior caso). Ao abrir:
 * clientes que a projeção põe ali, camadas, obra futura como potencial e, no
 * mês, a linha de realidade do PCP.
 *
 * "Produzido": o que a fábrica terminou nos períodos passados, contra o que
 * o ritmo da época esperava. Medido, sem cor.
 */
import React, { useCallback, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

import {
  CardItem,
  CardList,
  CardMeta,
  CardSub,
  CardTitulo,
  CardTopo,
} from "../../components/ListaCompacta/styles";
import { CAMADA, CAMADAS_DA_FILA, RELACAO } from "../../utils/capacidade";
import { plural } from "../../utils/dinheiro";
import { ROTULO_CAMADA, ddmm, textoProduzido } from "./apresentacao";
import { SELO_VENDA_ASSINADA } from "./apresentacaoVendas";
import { useAbrirCenario } from "./contextoReprogramar";
import ItemAmbiente, { rotuloPedidoCliente } from "./ItemAmbiente";
import {
  Bloco,
  BotaoTexto,
  CabecalhoPeriodo,
  Camadas,
  Detalhe,
  ItemLista,
  LinhaItem,
  LinkAmbiente,
  Lista,
  ListaSimples,
  Ponto,
  Regua,
  Selo,
} from "./styles";

function Situacao({ estado }) {
  if (!estado?.tom) return <span className="situacao">{estado?.texto || "—"}</span>;
  return (
    <span className="situacao">
      <Ponto $tom={estado.tom} aria-hidden="true" />
      {estado.texto}
    </span>
  );
}

function textoOcupacao(linha) {
  if (!linha.ocupacao || linha.ocupacao.semDiaUtil) return "—";
  return linha.ocupacao.texto;
}

function GrupoCliente({ grupo, podeAbrir, onAbrir }) {
  const abrirCenario = useAbrirCenario();
  const entregas = grupo.entregas.map(ddmm).join(", ") || "sem entrega";
  return (
    <LinhaItem>
      <div>
        <span className="nome" title={rotuloPedidoCliente(grupo)}>
          {rotuloPedidoCliente(grupo)}
          {grupo.vendaAssinadaId ? <Selo>{SELO_VENDA_ASSINADA}</Selo> : null}
          {grupo.podeAtrasar ? <Selo $tom="warning">pode atrasar</Selo> : null}
        </span>
        <span className="meta">
          {plural(grupo.ambientes, "ambiente", "ambientes")} · entrega {entregas}
        </span>
        <span className="sub">
          {grupo.itens.map((item, i) => (
            <React.Fragment key={item.identidade || `${item.chave}-${i}`}>
              {i > 0 ? " · " : ""}
              <LinkAmbiente type="button" disabled={!podeAbrir(item)} onClick={() => onAbrir(item)}>
                {item.ambiente}
              </LinkAmbiente>
            </React.Fragment>
          ))}
        </span>
        {/* Venda assinada não tem linha na planilha para reprogramar. */}
        {abrirCenario && !grupo.vendaAssinadaId ? (
          <BotaoTexto type="button" onClick={() => abrirCenario(grupo)} style={{ minHeight: 0, padding: "2px 0", justifySelf: "start", gridColumn: "1 / -1" }}>
            Reprogramar no Cenário do PCP
          </BotaoTexto>
        ) : null}
      </div>
    </LinhaItem>
  );
}

// A venda assinada só aparece na situação quando existe no período: na
// maioria dos meses ela não existe, e um "0" a mais seria ruído.
function camadasVisiveis(camadas) {
  return camadas[CAMADA.VENDA_ASSINADA] ? [...CAMADAS_DA_FILA, CAMADA.VENDA_ASSINADA] : CAMADAS_DA_FILA;
}

function DetalhePeriodo({ linha, podeAbrir, onAbrir }) {
  const camadas = linha.camadas || {};
  return (
    <Detalhe>
      {linha.relacao === RELACAO.CORRENTE && linha.produzido ? (
        <p>Já produzido neste período: {textoProduzido(linha.produzido)}.</p>
      ) : null}
      <div>
        <h3>Clientes que a fila põe aqui</h3>
        {linha.clientes.length === 0 ? (
          <p>Nenhum ambiente da fila cai neste período.</p>
        ) : (
          <>
            <ListaSimples>
              {linha.clientes.map((grupo) => (
                <GrupoCliente
                  key={`${grupo.pedido}|${grupo.cliente}|${grupo.vendaAssinadaId || ""}`}
                  grupo={grupo}
                  podeAbrir={podeAbrir}
                  onAbrir={onAbrir}
                />
              ))}
            </ListaSimples>
            <p>A posição é indicativa: o volume da fila é confiável, a ordem de saída não (a fábrica adianta clientes).</p>
          </>
        )}
      </div>
      <div>
        <h3>Situação dos ambientes</h3>
        <Camadas>
          {camadasVisiveis(camadas).map((camada) => (
            <span key={camada}>
              {ROTULO_CAMADA[camada]} <b>{camadas[camada] || 0}</b>
            </span>
          ))}
        </Camadas>
      </div>
      {linha.obraFutura.length > 0 ? (
        <div>
          <h3>Potencial (obra futura, fora da conta)</h3>
          <ListaSimples>
            {linha.obraFutura.map((item, i) => (
              <ItemAmbiente key={item.identidade || i} item={item} podeAbrir={podeAbrir} onAbrir={onAbrir} />
            ))}
          </ListaSimples>
        </div>
      ) : null}
      {linha.realidade ? (
        <div>
          <h3>Compromissos da planilha (para o PCP)</h3>
          <p>{linha.realidade.texto}.</p>
        </div>
      ) : null}
    </Detalhe>
  );
}

function useAbertos() {
  const [abertos, setAbertos] = useState(() => new Set());
  const alternar = useCallback((chave) => {
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  }, []);
  return [abertos, alternar];
}

function ProximosDesktop({ linhas, abertos, alternar, expandirTudo, podeAbrir, onAbrir }) {
  return (
    <Lista>
      <CabecalhoPeriodo as="div" className="colunas" aria-hidden="true">
        <span>Período</span>
        <span>Dias úteis</span>
        <span>Ocupação</span>
        <span>Situação</span>
        <span />
      </CabecalhoPeriodo>
      {linhas.map((linha) => {
        const aberto = expandirTudo || abertos.has(linha.chave);
        return (
          <ItemLista key={linha.chave} data-periodo={linha.chave}>
            <CabecalhoPeriodo type="button" aria-expanded={aberto} onClick={() => alternar(linha.chave)}>
              <span className="periodo" title={linha.rotulo}>
                {linha.rotulo}
                {linha.relacao === RELACAO.CORRENTE ? <Selo>atual</Selo> : null}
              </span>
              <span className="du">
                {linha.relacao === RELACAO.CORRENTE && linha.ocupacao && !linha.ocupacao.semDiaUtil
                  ? `${linha.ocupacao.restantes} de ${linha.diasUteis}`
                  : linha.diasUteis}
              </span>
              <span className="ocupacao">{textoOcupacao(linha)}</span>
              <Situacao estado={linha.estado} />
              <FiChevronDown className="seta" data-aberto={aberto ? "sim" : "nao"} size={16} aria-hidden="true" />
            </CabecalhoPeriodo>
            {aberto ? <DetalhePeriodo linha={linha} podeAbrir={podeAbrir} onAbrir={onAbrir} /> : null}
          </ItemLista>
        );
      })}
    </Lista>
  );
}

function ProximosCompacto({ linhas, abertos, alternar, expandirTudo, podeAbrir, onAbrir }) {
  return (
    <CardList>
      {linhas.map((linha) => {
        const aberto = expandirTudo || abertos.has(linha.chave);
        return (
          <div key={linha.chave} data-periodo={linha.chave} style={{ minWidth: 0 }}>
            <CardItem
              $clicavel
              role="button"
              tabIndex={0}
              aria-expanded={aberto}
              onClick={() => alternar(linha.chave)}
              onKeyDown={(evento) => {
                if (evento.key === "Enter" || evento.key === " ") {
                  evento.preventDefault();
                  alternar(linha.chave);
                }
              }}
            >
              <CardTopo>
                <CardTitulo>{linha.rotulo}</CardTitulo>
                <span className="selo">
                  <Situacao estado={linha.estado} />
                </span>
              </CardTopo>
              <CardSub>Ocupação {textoOcupacao(linha)}</CardSub>
              <CardMeta>
                <span className="par">
                  <span className="k">dias úteis</span> <b>{linha.diasUteis}</b>
                </span>
                <span className="par">
                  <span className="sep" aria-hidden="true">· </span>
                  <b>{linha.clientes.reduce((soma, g) => soma + g.ambientes, 0)}</b> <span className="k">ambientes da fila</span>
                </span>
              </CardMeta>
            </CardItem>
            {aberto ? <DetalhePeriodo linha={linha} podeAbrir={podeAbrir} onAbrir={onAbrir} /> : null}
          </div>
        );
      })}
    </CardList>
  );
}

function Produzidos({ linhas, compacto }) {
  if (compacto) {
    return (
      <CardList>
        {linhas.map((linha) => (
          <CardItem key={linha.chave}>
            <CardTopo>
              <CardTitulo>{linha.rotulo}</CardTitulo>
            </CardTopo>
            <CardSub>{textoProduzido(linha.produzido) || "—"}</CardSub>
            <CardMeta>
              <span className="par">
                <span className="k">dias úteis</span> <b>{linha.diasUteis}</b>
              </span>
            </CardMeta>
          </CardItem>
        ))}
      </CardList>
    );
  }
  return (
    <Lista>
      <CabecalhoPeriodo as="div" className="colunas" aria-hidden="true">
        <span>Período</span>
        <span>Dias úteis</span>
        <span style={{ gridColumn: "span 2" }}>Produzido</span>
        <span />
      </CabecalhoPeriodo>
      {linhas.map((linha) => (
        <ItemLista key={linha.chave}>
          <CabecalhoPeriodo as="div" style={{ cursor: "default" }}>
            <span className="periodo">{linha.rotulo}</span>
            <span className="du">{linha.diasUteis}</span>
            <span className="produzido" style={{ gridColumn: "span 2" }}>
              {textoProduzido(linha.produzido) || "—"}
            </span>
            <span />
          </CabecalhoPeriodo>
        </ItemLista>
      ))}
    </Lista>
  );
}

export default function Periodos({ periodos, compacto, expandirTudo, podeAbrir, onAbrir }) {
  const [abertos, alternar] = useAbertos();
  const Proximos = compacto ? ProximosCompacto : ProximosDesktop;
  return (
    <>
      <Bloco data-pdf="capacidade-periodos" aria-label="Agora e próximos">
        <h2>Agora e próximos</h2>
        <Proximos
          linhas={periodos.proximos}
          abertos={abertos}
          alternar={alternar}
          expandirTudo={expandirTudo}
          podeAbrir={podeAbrir}
          onAbrir={onAbrir}
        />
      </Bloco>
      {periodos.produzidos.length > 0 ? (
        <Bloco data-pdf="capacidade-produzido">
          <h2>Produzido</h2>
          <Regua>
            Ambientes terminados no período (término da marcenaria; carregamento quando o término falta) contra o que o
            ritmo medido naquela época esperava. Términos das 2 últimas semanas ainda estão chegando: provisório.
          </Regua>
          <Produzidos linhas={periodos.produzidos} compacto={compacto} />
        </Bloco>
      ) : null}
    </>
  );
}
